/**
 * Mock Backtest Data
 * 
 * This file contains realistic mock data for backtest visualization.
 * Data spans 6 months of trading with ~50 trades.
 */

// Helper to generate dates
const generateDateRange = (startDate, days, intervalMinutes = 1440) => {
  const dates = [];
  const start = new Date(startDate);
  const intervalsPerDay = 1440 / intervalMinutes;
  
  for (let i = 0; i < days * intervalsPerDay; i++) {
    const date = new Date(start.getTime() + i * intervalMinutes * 60 * 1000);
    // Skip weekends for daily data
    if (intervalMinutes >= 1440 && (date.getDay() === 0 || date.getDay() === 6)) {
      continue;
    }
    dates.push(date.toISOString());
  }
  return dates;
};

// Generate equity curve with realistic growth and drawdowns
const generateEquitySeries = () => {
  const startDate = '2024-01-01T09:15:00';
  const data = [];
  let equity = 100000;
  const dates = generateDateRange(startDate, 180, 1440); // Daily data for 6 months
  
  // Simulate realistic equity curve with trends and drawdowns
  let trend = 1;
  let volatility = 0.008;
  
  dates.forEach((timestamp, i) => {
    // Change trend occasionally
    if (Math.random() < 0.05) {
      trend = Math.random() > 0.4 ? 1 : -1;
    }
    
    // Daily return with trend bias
    const dailyReturn = (Math.random() - 0.45) * volatility * 2 + (trend * 0.001);
    equity = equity * (1 + dailyReturn);
    
    // Ensure equity doesn't go negative
    equity = Math.max(equity, 50000);
    
    data.push({
      timestamp,
      equity: Math.round(equity * 100) / 100
    });
  });
  
  return data;
};

// Generate returns series from equity
const generateReturnsSeries = (equitySeries) => {
  const returns = [];
  
  for (let i = 1; i < equitySeries.length; i++) {
    const prevEquity = equitySeries[i - 1].equity;
    const currEquity = equitySeries[i].equity;
    const dailyReturn = (currEquity - prevEquity) / prevEquity;
    
    returns.push({
      timestamp: equitySeries[i].timestamp,
      return: Math.round(dailyReturn * 10000) / 10000 // 4 decimal places
    });
  }
  
  return returns;
};

// Generate realistic trade log
const generateTrades = () => {
  const trades = [];
  const startDate = new Date('2024-01-02T10:00:00');
  let tradeId = 1;
  
  // Generate ~50 trades over 6 months
  for (let i = 0; i < 52; i++) {
    const entryDate = new Date(startDate.getTime() + i * 3.5 * 24 * 60 * 60 * 1000);
    
    // Skip weekends
    while (entryDate.getDay() === 0 || entryDate.getDay() === 6) {
      entryDate.setDate(entryDate.getDate() + 1);
    }
    
    // Random entry time during market hours (9:15 - 15:00)
    entryDate.setHours(9 + Math.floor(Math.random() * 5), Math.floor(Math.random() * 60), 0);
    
    // Trade duration: 30 mins to 5 days
    const durationMinutes = Math.floor(Math.random() * 7200) + 30;
    const exitDate = new Date(entryDate.getTime() + durationMinutes * 60 * 1000);
    
    // Adjust exit to market hours if needed
    if (exitDate.getHours() >= 15) {
      exitDate.setDate(exitDate.getDate() + 1);
      exitDate.setHours(10, 0, 0);
    }
    while (exitDate.getDay() === 0 || exitDate.getDay() === 6) {
      exitDate.setDate(exitDate.getDate() + 1);
    }
    
    const side = Math.random() > 0.5 ? 'BUY' : 'SELL';
    const entryPrice = 1800 + Math.random() * 400; // Price range 1800-2200
    
    // Win rate ~60%, with varying PnL
    const isWin = Math.random() < 0.6;
    const priceChange = (Math.random() * 0.03 + 0.005) * (isWin ? 1 : -1);
    const exitPrice = side === 'BUY' 
      ? entryPrice * (1 + priceChange)
      : entryPrice * (1 - priceChange);
    
    const qty = Math.floor(Math.random() * 20) + 5;
    const pnl = side === 'BUY'
      ? (exitPrice - entryPrice) * qty
      : (entryPrice - exitPrice) * qty;
    
    trades.push({
      trade_id: tradeId++,
      entry_time: entryDate.toISOString(),
      exit_time: exitDate.toISOString(),
      entry_price: Math.round(entryPrice * 100) / 100,
      exit_price: Math.round(exitPrice * 100) / 100,
      qty,
      side,
      pnl: Math.round(pnl * 100) / 100,
      return: Math.round((pnl / (entryPrice * qty)) * 10000) / 10000
    });
  }
  
  return trades;
};

// Generate exposure timeline
const generateExposureSeries = (equitySeries, trades) => {
  const exposure = [];
  
  equitySeries.forEach(({ timestamp }) => {
    const date = new Date(timestamp);
    
    // Check if any trade is active at this timestamp
    const activeTrades = trades.filter(trade => {
      const entryTime = new Date(trade.entry_time);
      const exitTime = new Date(trade.exit_time);
      return date >= entryTime && date <= exitTime;
    });
    
    // Calculate exposure percentage (simplified)
    const exposurePct = activeTrades.length > 0 
      ? Math.min(activeTrades.length * 25, 100) 
      : 0;
    
    exposure.push({
      timestamp,
      exposure_pct: exposurePct
    });
  });
  
  return exposure;
};

// Generate OHLCV price series
const generatePriceSeries = () => {
  const startDate = '2024-01-01T09:15:00';
  const data = [];
  let basePrice = 1950;
  const dates = generateDateRange(startDate, 180, 1440);
  
  dates.forEach((timestamp) => {
    // Random walk with mean reversion
    const change = (Math.random() - 0.5) * 40;
    basePrice = basePrice + change;
    basePrice = Math.max(1700, Math.min(2300, basePrice)); // Keep in range
    
    const open = basePrice + (Math.random() - 0.5) * 20;
    const close = basePrice + (Math.random() - 0.5) * 20;
    const high = Math.max(open, close) + Math.random() * 15;
    const low = Math.min(open, close) - Math.random() * 15;
    const volume = Math.floor(Math.random() * 500000) + 100000;
    
    data.push({
      timestamp,
      open: Math.round(open * 100) / 100,
      high: Math.round(high * 100) / 100,
      low: Math.round(low * 100) / 100,
      close: Math.round(close * 100) / 100,
      volume
    });
  });
  
  return data;
};

// Generate benchmark series (e.g., NIFTY 50)
const generateBenchmarkSeries = (equitySeries) => {
  let benchmarkPrice = 21500;
  
  return equitySeries.map(({ timestamp }) => {
    const change = (Math.random() - 0.48) * 150; // Slight upward bias
    benchmarkPrice = benchmarkPrice + change;
    benchmarkPrice = Math.max(20000, Math.min(24000, benchmarkPrice));
    
    return {
      timestamp,
      benchmark_price: Math.round(benchmarkPrice * 100) / 100
    };
  });
};

// Calculate summary metrics
const calculateSummary = (equitySeries, returnsSeries, trades) => {
  const initialEquity = equitySeries[0].equity;
  const finalEquity = equitySeries[equitySeries.length - 1].equity;
  const totalReturn = (finalEquity - initialEquity) / initialEquity;
  
  // Calculate max drawdown
  let peak = initialEquity;
  let maxDrawdown = 0;
  equitySeries.forEach(({ equity }) => {
    if (equity > peak) peak = equity;
    const drawdown = (peak - equity) / peak;
    if (drawdown > maxDrawdown) maxDrawdown = drawdown;
  });
  
  // Calculate Sharpe ratio (simplified, assuming risk-free rate = 0)
  const returns = returnsSeries.map(r => r.return);
  const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
  const stdDev = Math.sqrt(
    returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length
  );
  const sharpeRatio = (avgReturn * 252) / (stdDev * Math.sqrt(252)); // Annualized
  
  // Trade statistics
  const winningTrades = trades.filter(t => t.pnl > 0);
  const losingTrades = trades.filter(t => t.pnl <= 0);
  const winRate = winningTrades.length / trades.length;
  
  const grossProfit = winningTrades.reduce((sum, t) => sum + t.pnl, 0);
  const grossLoss = Math.abs(losingTrades.reduce((sum, t) => sum + t.pnl, 0));
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;
  
  // Average trade duration
  const avgDurationMs = trades.reduce((sum, t) => {
    return sum + (new Date(t.exit_time) - new Date(t.entry_time));
  }, 0) / trades.length;
  const avgDurationHours = Math.round(avgDurationMs / (1000 * 60 * 60));
  
  return {
    totalReturn: Math.round(totalReturn * 10000) / 100, // As percentage
    sharpeRatio: Math.round(sharpeRatio * 100) / 100,
    maxDrawdown: Math.round(maxDrawdown * 10000) / 100, // As percentage
    winRate: Math.round(winRate * 100),
    profitFactor: Math.round(profitFactor * 100) / 100,
    totalTrades: trades.length,
    winningTrades: winningTrades.length,
    losingTrades: losingTrades.length,
    avgTradeDuration: `${avgDurationHours}h`,
    grossProfit: Math.round(grossProfit),
    grossLoss: Math.round(grossLoss),
    netProfit: Math.round(grossProfit - grossLoss),
    initialCapital: initialEquity,
    finalCapital: Math.round(finalEquity)
  };
};

// Generate all data
const equitySeries = generateEquitySeries();
const returnsSeries = generateReturnsSeries(equitySeries);
const trades = generateTrades();
const exposureSeries = generateExposureSeries(equitySeries, trades);
const priceSeries = generatePriceSeries();
const benchmarkSeries = generateBenchmarkSeries(equitySeries);
const summary = calculateSummary(equitySeries, returnsSeries, trades);

export const mockBacktestData = {
  strategyName: 'Trend Following Strategy',
  symbol: 'NIFTY',
  timeframe: '1D',
  startDate: equitySeries[0].timestamp,
  endDate: equitySeries[equitySeries.length - 1].timestamp,
  
  // Core data series
  equitySeries,
  returnsSeries,
  trades,
  exposureSeries,
  priceSeries,
  benchmarkSeries,
  
  // Summary metrics
  summary
};

export default mockBacktestData;
